import React, { useCallback, useEffect, useState } from 'react';
import ApiService from '../../../services/ApiService';
import { convertDataToFloorStructure } from '../../../services/common.utils';
import CompactRoomMonitorCellWithDoorHover from '../Monitor/CompactRoomMonitorCellWithDoorHover';
import { BADGE_DISPLAY_GUEST } from '../../../services/monitorBadgeDisplay';

/** First initial + last name when /getbulkroomassignments includes guest_first / guest_last (optional). */
function formatBulkGuestName(row) {
    const rawFirst = row.guest_first ?? row.guest_first_name;
    const rawLast = row.guest_last ?? row.guest_last_name;
    const first = rawFirst != null && String(rawFirst).trim() !== '' ? String(rawFirst).trim() : '';
    const last = rawLast != null && String(rawLast).trim() !== '' ? String(rawLast).trim() : '';
    if (first && last) return `${first.charAt(0).toUpperCase()}. ${last}`;
    if (last) return last;
    if (first) return first;
    return '—';
}

function formatBulkDate(iso) {
    if (iso == null || iso === '') return '—';
    const d = new Date(iso);
    return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

function getRoomDataForBulkRow(roomDataMap, roomNumber) {
    if (roomNumber == null || roomNumber === '') return undefined;
    const s = String(roomNumber).trim();
    if (roomDataMap[s] != null) return roomDataMap[s];
    const n = Number(s);
    if (!Number.isNaN(n) && roomDataMap[String(n)] != null) return roomDataMap[String(n)];
    return undefined;
}

/**
 * Modal for bulk room assignment (Change Assignment flow).
 * Step 1: User sets min_match_pct and spacing_mode, clicks "See Matches" → PUT /getbulkroomassignments.
 * Step 2: User selects/deselects rows, clicks "Process Selected" → POST /processbulkroomassignments
 *         with an array of { pms_pk_id, external_id } (always an array, even for one row).
 */
const BulkRoomAssignmentModal = ({
    show,
    onClose,
    onSuccess,
    propertiesId,
    pmsPropertiesId,
    showDate
}) => {
    const [minMatchPct, setMinMatchPct] = useState(0);
    const [showNoMatchesModal, setShowNoMatchesModal] = useState(false);
    /** 1 = parameters only, 2 = results only (never both visible) */
    const [activeStep, setActiveStep] = useState(1);
    const [spacingMode, setSpacingMode] = useState('random');
    const [results, setResults] = useState([]);
    const [selectedIndices, setSelectedIndices] = useState(new Set());
    const [roomDataMap, setRoomDataMap] = useState({});
    const [loading, setLoading] = useState(false);
    const [processing, setProcessing] = useState(false);
    const [error, setError] = useState(null);

    /** Full reset whenever the modal opens or closes so nothing is cached between sessions. */
    const resetModalState = useCallback(() => {
        setMinMatchPct(0);
        setShowNoMatchesModal(false);
        setActiveStep(1);
        setSpacingMode('random');
        setResults([]);
        setSelectedIndices(new Set());
        setRoomDataMap({});
        setLoading(false);
        setProcessing(false);
        setError(null);
    }, []);

    useEffect(() => {
        resetModalState();
        if (show) {
            const data = {
                show,
                propertiesId,
                pmsPropertiesId,
                hasPropertiesId: propertiesId != null && propertiesId !== '',
                hasPmsPropertiesId: pmsPropertiesId != null && pmsPropertiesId !== ''
            };
            console.log('[BulkRoomAssignmentModal] opened with data:', data);
        }
    }, [show, resetModalState]);

    const fetchRoomStatuses = useCallback(async (properties_id, show_date) => {
        const res = await ApiService.post('/getroomstatuses', { properties_id, show_date });
        const rooms = res?.data?.rooms_array || [];
        const floors = convertDataToFloorStructure(rooms);
        const map = {};
        floors.forEach((floor) => {
            floor.rooms.forEach((room) => {
                const num = room?.roomNumber ?? room?.room_number;
                if (num == null || num === '') return;
                const key = String(num).trim();
                map[key] = room;
                const n = Number(key);
                if (!Number.isNaN(n)) map[String(n)] = room;
            });
        });
        return map;
    }, []);

    const handleSeeMatches = async () => {
        const hasPropertiesId = propertiesId != null && propertiesId !== '';
        const hasPmsPropertiesId = pmsPropertiesId != null && pmsPropertiesId !== '';
        if (!hasPropertiesId || !hasPmsPropertiesId) {
            setError('Property configuration missing (properties_id and pms_properties_id required).');
            return;
        }
        setError(null);
        setShowNoMatchesModal(false);
        setLoading(true);
        setResults([]);
        setSelectedIndices(new Set());
        setRoomDataMap({});
        try {
            const response = await ApiService.put('/getbulkroomassignments', {
                properties_id: Number(propertiesId),
                pms_properties_id: Number(pmsPropertiesId),
                min_match_pct: Number(minMatchPct),
                spacing_mode: spacingMode,
                show_date: showDate,
            });
            const data = response?.data;
            const list = Array.isArray(data) ? data : (data?.assignments ?? data?.data ?? []);
            setResults(list);
            if (list.length === 0) {
                setShowNoMatchesModal(true);
                setSelectedIndices(new Set());
                setActiveStep(1);
            } else {
                setSelectedIndices(new Set(list.map((_, i) => i)));
                setActiveStep(2);
                try {
                    const roomMap = await fetchRoomStatuses(Number(propertiesId), showDate);
                    setRoomDataMap(roomMap);
                } catch (roomErr) {
                    console.warn('Could not load room statuses for doors:', roomErr);
                }
            }
        } catch (err) {
            console.error('getbulkroomassignments error:', err);
            const msg = err?.response?.data?.message || err?.message || 'Failed to load matches';
            setError(msg);
            setResults([]);
        } finally {
            setLoading(false);
        }
    };

    const toggleIndex = (index) => {
        setSelectedIndices((prev) => {
            const next = new Set(prev);
            if (next.has(index)) next.delete(index);
            else next.add(index);
            return next;
        });
    };

    const toggleAll = (checked) => {
        if (checked) setSelectedIndices(new Set(results.map((_, i) => i)));
        else setSelectedIndices(new Set());
    };

    const handleProcessSelected = async () => {
        const selected = results
            .filter((_, i) => selectedIndices.has(i))
            .map((row) => ({
                pms_pk_id: row.pms_pk_id,
                external_id: row.external_id
            }));
        if (selected.length === 0) {
            setError('Select at least one assignment to process.');
            return;
        }
        setError(null);
        setProcessing(true);
        try {
            // Always POST an array (even for one record); hub wraps arrays as api_body.
            await ApiService.post('/processbulkroomassignments', selected);
            if (onSuccess) onSuccess();
            onClose();
        } catch (err) {
            console.error('processbulkroomassignments error:', err);
            const msg = err?.response?.data?.message || err?.message || 'Failed to process assignments';
            setError(msg);
        } finally {
            setProcessing(false);
        }
    };

    const allSelected = results.length > 0 && selectedIndices.size === results.length;
    const someSelected = selectedIndices.size > 0;
    const showStep1 = activeStep === 1;
    const showStep2 = activeStep === 2 && results.length > 0;

    if (!show) return null;

    return (
        <div
            className="modal show d-block"
            tabIndex={-1}
            role="dialog"
            style={{
                backgroundColor: 'rgba(0,0,0,0.5)',
                zIndex: 2100,
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                overflow: 'auto',
                padding: '0.75rem'
            }}
        >
            <div
                className="modal-dialog modal-xl modal-dialog-centered modal-dialog-scrollable w-100 mx-0 mx-md-auto"
                role="document"
                style={{ maxWidth: 1100 }}
                onClick={(e) => e.stopPropagation()}
            >
                <div className="modal-content">
                    <div className="modal-header">
                        <h5 className="modal-title">Change assignment — bulk room matches</h5>
                        <button type="button" className="btn-close" aria-label="Close" onClick={onClose} />
                    </div>
                    <div className="modal-body">
                        {showStep1 && (
                        <div className="card shadow-sm border-0 mb-4 checkData">
                            <div className="card-body">
                                <h6 className="fw-bold mb-3">Step 1 — Get matches</h6>
                                <div className="row g-3 align-items-end">
                                    <div className="col-12 col-sm-6 col-md-4 mb-2 mb-md-0">
                                        <label className="form-label fw-bold mb-1">Min match %</label>
                                        <input
                                            type="number"
                                            className="form-control"
                                            min={0}
                                            max={100}
                                            value={minMatchPct}
                                            onChange={(e) => setMinMatchPct(Number(e.target.value) || 0)}
                                            style={{ maxWidth: '120px' }}
                                        />
                                        <small className="text-muted d-block mt-1" style={{ minHeight: '2.25rem' }}>
                                            Matches at or above this % (e.g. 75 = 75% and higher)
                                        </small>
                                    </div>
                                    <div className="col-12 col-sm-6 col-md-4 mb-2 mb-md-0">
                                        <label className="form-label fw-bold mb-1">Spacing mode</label>
                                        <select
                                            className="form-select"
                                            value={spacingMode}
                                            onChange={(e) => setSpacingMode(e.target.value)}
                                            style={{ maxWidth: '180px' }}
                                        >
                                            <option value="random">Random</option>
                                            <option value="cluster">Cluster</option>
                                        </select>
                                        <small className="text-muted d-block mt-1" style={{ minHeight: '2.25rem' }}>
                                            Distribution of assignments across rooms
                                        </small>
                                    </div>
                                    <div className="col-12 col-sm-12 col-md-4 d-flex flex-column align-items-start align-items-md-end">
                                        <label className="form-label fw-bold mb-1 invisible">Action</label>
                                        <button
                                            type="button"
                                            className="btn btn-primary w-100 w-md-auto"
                                            style={{ minHeight: '38px' }}
                                            onClick={handleSeeMatches}
                                            disabled={loading}
                                        >
                                            {loading ? 'Loading…' : results.length > 0 ? 'Refresh Matches' : 'See Matches'}
                                        </button>
                                        <span className="d-block mt-1" style={{ minHeight: '2.25rem' }} />
                                    </div>
                                </div>
                                {results.length > 0 && (
                                    <div className="mt-3 pt-3 border-top">
                                        <button
                                            type="button"
                                            className="btn btn-outline-primary"
                                            onClick={() => setActiveStep(2)}
                                        >
                                            Continue to step 2 — select assignments
                                        </button>
                                        <small className="d-block text-muted mt-2 mb-0">
                                            Uses your current match results without running the search again.
                                        </small>
                                    </div>
                                )}
                            </div>
                        </div>
                        )}

                        {error && (
                            <div className="alert alert-danger mb-3" role="alert">
                                <i className="bi bi-exclamation-triangle me-2" />
                                {error}
                            </div>
                        )}

                        {/* Step 2: Results list */}
                        {showStep2 && (
                            <div className="card shadow-sm border-0 checkData">
                                <div className="card-body">
                                    <div className="d-flex flex-column flex-sm-row justify-content-between align-items-start align-items-sm-center gap-2 mb-3">
                                        <div className="d-flex flex-column flex-sm-row align-items-start align-items-sm-center gap-2">
                                            {/* <button
                                                type="button"
                                                className="btn btn-sm btn-outline-secondary"
                                                onClick={() => setActiveStep(1)}
                                            >
                                                ← Back to step 1
                                            </button> */}
                                            <div>
                                                <h6 className="fw-bold mb-0">Step 2 — Select assignments to apply</h6>
                                                <p className="text-muted small mb-0 mt-1">
                                                    Guest name appears when the API includes guest_first and guest_last on each row.
                                                </p>
                                            </div>
                                        </div>
                                        <label className="mb-0 d-flex align-items-center gap-2">
                                            <input
                                                type="checkbox"
                                                checked={allSelected}
                                                ref={(el) => el && (el.indeterminate = someSelected && !allSelected)}
                                                onChange={(e) => toggleAll(e.target.checked)}
                                            />
                                            Select all
                                        </label>
                                    </div>
                                    <div className="table-responsive" style={{ maxHeight: '60vh', overflowY: 'auto' }}>
                                        <table className="table table-sm table-bordered mb-0">
                                            <thead className="table-light sticky-top">
                                                <tr>
                                                    <th style={{ width: 40 }}>Include</th>
                                                    <th>Confirmation #</th>
                                                    <th>Guest Name</th>
                                                    <th>Arrival</th>
                                                    <th>Departure</th>
                                                    <th>Room</th>
                                                    <th>Match %</th>
                                                    <th>Likes</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {results.map((row, index) => {
                                                    const roomData = getRoomDataForBulkRow(roomDataMap, row.room_number);
                                                    return (
                                                        <tr key={index}>
                                                            <td className="align-middle text-center">
                                                                <input
                                                                    type="checkbox"
                                                                    checked={selectedIndices.has(index)}
                                                                    onChange={() => toggleIndex(index)}
                                                                />
                                                            </td>
                                                            <td className="align-middle fw-bold text-center text-primary">{row.confirmation_number}</td>
                                                            <td
                                                                className="align-middle text-center text-nowrap small"
                                                                title={
                                                                    formatBulkGuestName(row) === '—'
                                                                        ? 'Guest name when the API includes guest_first and guest_last'
                                                                        : undefined
                                                                }
                                                            >
                                                                {formatBulkGuestName(row)}
                                                            </td>
                                                            <td className="align-middle text-center text-nowrap small">
                                                                {formatBulkDate(row.arrival_date)}
                                                            </td>
                                                            <td className="align-middle text-center text-nowrap small">
                                                                {formatBulkDate(row.departure_date)}
                                                            </td>
                                                            <td className="align-middle py-2 px-1 text-center">
                                                                <div className="d-flex justify-content-center align-items-start">
                                                                    {roomData ? (
                                                                        <CompactRoomMonitorCellWithDoorHover
                                                                            room={roomData}
                                                                            badgeDisplayMode={BADGE_DISPLAY_GUEST}
                                                                            onBadgeClick={() => {}}
                                                                            onConfirmationClick={() => {}}
                                                                            overlayRootZIndex={2150}
                                                                        />
                                                                    ) : (
                                                                        <div
                                                                            className="border rounded p-2 text-center"
                                                                            style={{
                                                                                backgroundColor: '#f8f9fa',
                                                                                minWidth: 42,
                                                                                fontSize: '0.75rem',
                                                                                fontWeight: 'bold'
                                                                            }}
                                                                        >
                                                                            {row.room_number}
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            </td>
                                                            <td className="align-middle text-center">
                                                                <span className="text-green fw-bold">{row.match_pct ?? 0}%</span>
                                                            </td>
                                                            <td className="align-middle small" style={{ maxWidth: 260 }}>
                                                                {row.likes_checklist && row.likes_checklist.length > 0 ? (
                                                                    <>
                                                                        <div className="text-muted fw-semibold mb-1">
                                                                            {row.matched_likes ?? 0} / {row.total_likes ?? row.likes_checklist.length}{' '}
                                                                            matched
                                                                        </div>
                                                                        <ul className="mb-0 ps-0" style={{ listStyle: 'none' }}>
                                                                            {[...row.likes_checklist]
                                                                                .sort((a, b) => (b.is_match || 0) - (a.is_match || 0))
                                                                                .map((l, i) => (
                                                                                <li key={i} className="text-nowrap text-truncate" title={l.like_name}>
                                                                                    {l.is_match === 1 ? (
                                                                                        <span className="text-muted">
                                                                                            <i
                                                                                                className="bi bi-check"
                                                                                                style={{
                                                                                                    color: '#28a745',
                                                                                                    fontSize: '1.25rem',
                                                                                                    marginRight: '5px',
                                                                                                    opacity: 1
                                                                                                }}
                                                                                            />
                                                                                            {l.like_name}
                                                                                        </span>
                                                                                    ) : (
                                                                                        <span className="text-muted">
                                                                                            <i
                                                                                                className="bi bi-x"
                                                                                                style={{
                                                                                                    color: '#dc3545',
                                                                                                    fontSize: '1.25rem',
                                                                                                    marginRight: '5px',
                                                                                                    opacity: 1
                                                                                                }}
                                                                                            />
                                                                                            {l.like_name}
                                                                                        </span>
                                                                                    )}
                                                                                </li>
                                                                            ))}
                                                                        </ul>
                                                                    </>
                                                                ) : row.total_likes != null || row.matched_likes != null ? (
                                                                    <span className="text-muted">
                                                                        {row.matched_likes ?? 0} / {row.total_likes ?? 0} matched
                                                                    </span>
                                                                ) : (
                                                                    <span className="text-muted">—</span>
                                                                )}
                                                            </td>
                                                        </tr>
                                                    );
                                                })}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                    <div
                        className={`modal-footer flex-wrap gap-2 align-items-center ${
                            showStep2 ? 'justify-content-between' : 'justify-content-end'
                        }`}
                    >
                        {showStep2 && (
                            <button
                                type="button"
                                className="btn btn-outline-secondary"
                                style={{ minHeight: '38px' }}
                                onClick={() => setActiveStep(1)}
                            >
                                Back to step 1
                            </button>
                        )}
                        <div className="d-flex flex-wrap gap-2">
                            {showStep2 && (
                                <button
                                    type="button"
                                    className="btn"
                                    style={{
                                        backgroundColor: '#20c997',
                                        borderColor: '#20c997',
                                        color: '#fff',
                                        minHeight: '38px'
                                    }}
                                    onClick={handleProcessSelected}
                                    disabled={processing || !someSelected}
                                >
                                    {processing ? 'Processing…' : `Process ${selectedIndices.size} selected`}
                                </button>
                            )}
                            <button
                                type="button"
                                className="btn btn-secondary w-auto"
                                style={{ minHeight: '38px' }}
                                onClick={onClose}
                            >
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {showNoMatchesModal && (
                <div
                    className="modal show d-block"
                    role="dialog"
                    aria-modal="true"
                    aria-labelledby="noMatchesTitle"
                    style={{
                        backgroundColor: 'rgba(0,0,0,0.45)',
                        zIndex: 2200,
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0
                    }}
                    onClick={(e) => e.target === e.currentTarget && setShowNoMatchesModal(false)}
                >
                    <div className="modal-dialog modal-dialog-centered" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-content shadow">
                            <div className="modal-header border-0 pb-0">
                                <h5 className="modal-title" id="noMatchesTitle">
                                    No room matches found
                                </h5>
                                <button
                                    type="button"
                                    className="btn-close"
                                    aria-label="Close"
                                    onClick={() => setShowNoMatchesModal(false)}
                                />
                            </div>
                            <div className="modal-body pt-2">
                                <p className="text-muted mb-0">
                                    Try lowering the minimum match % or changing spacing mode, then run <strong>See Matches</strong> again.
                                </p>
                            </div>
                            <div className="modal-footer border-0 pt-0">
                                <button
                                    type="button"
                                    className="btn btn-primary"
                                    onClick={() => setShowNoMatchesModal(false)}
                                >
                                    OK
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default BulkRoomAssignmentModal;
